import React, { useState, useEffect, useRef } from "react";
import { Chart } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from "chart.js";
import { CandlestickController, CandlestickElement } from "chartjs-chart-financial";

ChartJS.register(CategoryScale, LinearScale, Tooltip, Legend, CandlestickController, CandlestickElement);

const ImpliedMovesChart = () => {
    const [ohlcData, setOhlcData] = useState([]);
    const [dailyPct, setDailyPct] = useState(null);
    const [loading, setLoading] = useState(true);
    const chartRef = useRef(null);
    const API_URL = process.env.REACT_APP_API_URL || "";

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [ndjsonRes, volRes] = await Promise.all([
                    fetch(`${API_URL}/data/momentum/SPX`),
                    fetch(`${API_URL}/data/volatility`),
                ]);
                const { data: ohlcRaw } = await ndjsonRes.json();
                const ohlcAll = ohlcRaw.slice(-20);
                const { data: volAll } = await volRes.json();
                setOhlcData(ohlcAll);
                const latestVol = [...volAll].reverse().find(d => d.implied_vol != null);
                if (latestVol) setDailyPct(latestVol.implied_vol);
            } catch (err) {
                console.error("Error fetching implied moves data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [API_URL]);

    if (loading) return <div style={{ textAlign: "center", padding: "20px" }}>Loading...</div>;
    if (!ohlcData.length || dailyPct == null) return <div style={{ textAlign: "center", padding: "20px" }}>No data available</div>;

    const latestCandle = ohlcData[ohlcData.length - 1];
    const currentClose = parseFloat(latestCandle.close);
    const latestDate   = latestCandle.date;

    const weeklyPct  = dailyPct * Math.sqrt(5);
    const annualizedIV = (dailyPct * Math.sqrt(252)).toFixed(1);

    const monthlyPct = dailyPct * Math.sqrt(21);

    const dailyHigh   = currentClose * (1 + dailyPct   / 100);
    const dailyLow    = currentClose * (1 - dailyPct   / 100);
    const weeklyHigh  = currentClose * (1 + weeklyPct  / 100);
    const weeklyLow   = currentClose * (1 - weeklyPct  / 100);
    const monthlyHigh = currentClose * (1 + monthlyPct / 100);
    const monthlyLow  = currentClose * (1 - monthlyPct / 100);

    const futureLabels = [" ", "+1D", "+2D", "+3D", "+4D", "+1W"];
    const labels = [
        ...ohlcData.map(d => {
            const [y, mo, day] = d.date.split("-");
            return new Date(y, mo - 1, day).toLocaleDateString("en-US", { month: "short", day: "numeric" });
        }),
        ...futureLabels,
    ];

    const candleData = ohlcData.map((d, i) => ({
        x: i,
        o: parseFloat(d.open),
        h: parseFloat(d.high),
        l: parseFloat(d.low),
        c: parseFloat(d.close),
    }));

    const allPrices = ohlcData.flatMap(d => [d.open, d.high, d.low, d.close].map(parseFloat));
    const yMin = Math.min(monthlyLow,  ...allPrices) * 0.9975;
    const yMax = Math.max(monthlyHigh, ...allPrices) * 1.0025;

    // Inline plugin: draws horizontal lines + shaded bands directly on the canvas
    const impliedLinesPlugin = {
        id: "impliedLines",
        afterDraw(chart) {
            const { ctx, chartArea: { left, right, top, bottom }, scales: { y } } = chart;

            const levels = [
                { value: monthlyHigh, color: "rgba(65,105,225,0.4)",  dash: [10, 6], width: 1.5, label: `+1M +${monthlyPct.toFixed(2)}%  $${monthlyHigh.toFixed(0)}` },
                { value: monthlyLow,  color: "rgba(220,53,69,0.4)",   dash: [10, 6], width: 1.5, label: `-1M −${monthlyPct.toFixed(2)}%  $${monthlyLow.toFixed(0)}`  },
                { value: weeklyHigh,  color: "rgba(65,105,225,0.65)", dash: [7, 4],  width: 1.5, label: `+1W +${weeklyPct.toFixed(2)}%  $${weeklyHigh.toFixed(0)}`  },
                { value: weeklyLow,   color: "rgba(220,53,69,0.65)",  dash: [7, 4],  width: 1.5, label: `-1W −${weeklyPct.toFixed(2)}%  $${weeklyLow.toFixed(0)}`   },
                { value: dailyHigh,   color: "rgba(30,80,210,1)",     dash: [5, 3],  width: 2,   label: `+1D +${dailyPct.toFixed(2)}%  $${dailyHigh.toFixed(0)}`   },
                { value: dailyLow,    color: "rgba(200,35,35,1)",     dash: [5, 3],  width: 2,   label: `-1D −${dailyPct.toFixed(2)}%  $${dailyLow.toFixed(0)}`    },
            ];

            // Shaded fill: monthly band (outermost, lightest)
            const mTop = y.getPixelForValue(monthlyHigh);
            const mBot = y.getPixelForValue(monthlyLow);
            ctx.save();
            ctx.fillStyle = "rgba(120,140,210,0.06)";
            ctx.fillRect(left, Math.max(top, mTop), right - left, Math.min(bottom, mBot) - Math.max(top, mTop));
            ctx.restore();

            // Shaded fill: weekly band
            const wTop = y.getPixelForValue(weeklyHigh);
            const wBot = y.getPixelForValue(weeklyLow);
            ctx.save();
            ctx.fillStyle = "rgba(120,140,210,0.10)";
            ctx.fillRect(left, Math.max(top, wTop), right - left, Math.min(bottom, wBot) - Math.max(top, wTop));
            ctx.restore();

            // Shaded fill: daily band (innermost, darkest)
            const dTop = y.getPixelForValue(dailyHigh);
            const dBot = y.getPixelForValue(dailyLow);
            ctx.save();
            ctx.fillStyle = "rgba(100,120,200,0.15)";
            ctx.fillRect(left, Math.max(top, dTop), right - left, Math.min(bottom, dBot) - Math.max(top, dTop));
            ctx.restore();

            // Draw each horizontal line + label
            levels.forEach(({ value, color, dash, width, label }) => {
                const py = y.getPixelForValue(value);
                if (py < top || py > bottom) return;
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(left, py);
                ctx.lineTo(right, py);
                ctx.strokeStyle = color;
                ctx.lineWidth = width;
                ctx.setLineDash(dash);
                ctx.stroke();
                // Label on right edge
                ctx.setLineDash([]);
                ctx.fillStyle = color;
                ctx.font = "bold 11px sans-serif";
                ctx.textAlign = "right";
                ctx.textBaseline = "bottom";
                ctx.fillText(label, right - 6, py - 2);
                ctx.restore();
            });
        },
    };

    const chartData = {
        labels,
        datasets: [{
            type: "candlestick",
            label: "SPX",
            data: candleData,
            color: { up: "rgb(0,150,0)", down: "rgb(210,40,40)", unchanged: "rgb(130,130,130)" },
            borderColor: { up: "rgb(0,150,0)", down: "rgb(210,40,40)", unchanged: "rgb(130,130,130)" },
        }],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: (items) => labels[items[0]?.dataIndex] ?? "",
                    label: (ctx) => {
                        const raw = ctx.raw;
                        if (!raw || typeof raw !== "object") return null;
                        return [`O: $${raw.o.toFixed(2)}`, `H: $${raw.h.toFixed(2)}`, `L: $${raw.l.toFixed(2)}`, `C: $${raw.c.toFixed(2)}`];
                    },
                },
            },
        },
        scales: {
            x: {
                type: "category",
                offset: true,
                grid: { offset: true, color: "rgba(200,200,200,0.2)" },
                ticks: {
                    font: { size: 10 },
                    maxRotation: 45,
                    autoSkip: false,
                    callback: (_, idx) => labels[idx] || null,
                },
            },
            y: {
                type: "linear",
                min: yMin,
                max: yMax,
                title: { display: true, text: "SPX Price ($)" },
                grid: { color: "rgba(200,200,200,0.2)" },
                ticks: { callback: (v) => `$${v.toFixed(0)}` },
            },
        },
    };

    return (
        <div style={{ width: "100%" }}>
            <div style={{ marginBottom: "6px", fontSize: "12px", color: "#888" }}>
                As of {latestDate} — IV: <strong>{annualizedIV}%</strong> ann. | Close: <strong style={{ color: "#444" }}>${currentClose.toFixed(2)}</strong>
                <span style={{ marginLeft: "10px" }}>1M: ±{monthlyPct.toFixed(2)}%</span>
            </div>
            <div style={{ height: "370px" }}>
                <Chart
                    ref={chartRef}
                    type="candlestick"
                    data={chartData}
                    options={options}
                    plugins={[impliedLinesPlugin]}
                />
            </div>
        </div>
    );
};

export default ImpliedMovesChart;
